"use client";

import React, { useMemo, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight, Plus } from "lucide-react";
type Step = {
  label: string;
  cmd: string;
  duration: string;
  status: string;
  body: string;
  bullets: string[];
};
type Phase = {
  id: string;
  index: number;
  cmd: string;
  title: string;
  tag: string;
  duration: string;
  summary: string;
  outcome: string;
  steps: Step[];
};
const phases: Phase[] = [{
  id: "discovery",
  index: 1,
  cmd: "rtw discover --environment --baseline",
  title: "Discovery",
  tag: "ASSESS",
  duration: "~1–2 weeks",
  summary: "Before we touch a single setting, we learn your environment cold — how your people work, what's breaking, and what's quietly putting you at risk.",
  outcome: "A complete picture of your environment and a prioritized list of what to fix first.",
  steps: [{
    label: "Environment walkthrough",
    cmd: "scan --network --endpoints --cloud",
    duration: "2–3 days",
    status: "Mapping",
    body: "We sit down with your team and walk every corner of the environment — workstations, servers, network gear, cloud tenants, line-of-business apps, and the shadow IT nobody admits to. We document what exists, how it's connected, and who depends on it.",
    bullets: ["Inventory endpoints, servers, switches, firewalls, and Wi-Fi", "Map cloud tenants, SaaS apps, and identity providers", "Catalog line-of-business and vertical-specific software"]
  }, {
    label: "Pain point interviews",
    cmd: "interview --stakeholders --frontline",
    duration: "1–2 days",
    status: "Listening",
    body: "Tech only matters if it serves the people using it. We interview leadership and frontline staff to surface the daily friction — the slow logins, the printer that never works, the app that crashes every Friday — and the strategic goals IT needs to support.",
    bullets: ["Leadership goals and growth plans", "Recurring frustrations and productivity blockers", "Compliance, industry, and client-driven requirements"]
  }, {
    label: "Infrastructure & security baseline",
    cmd: "audit --posture --risk",
    duration: "2–4 days",
    status: "Auditing",
    body: "We benchmark the current state against modern security and operational standards: patch levels, backup status, identity hygiene, exposed services, and aging hardware. Every gap gets logged with a risk rating.",
    bullets: ["Patch, firmware, and end-of-life hardware review", "Backup coverage and recoverability check", "Identity, MFA, and access-control posture"]
  }, {
    label: "Findings & roadmap",
    cmd: "report --findings --prioritize",
    duration: "2–3 days",
    status: "Synthesizing",
    body: "We bring it all together into a plain-English findings report — no jargon wall. You see exactly where you stand, what's urgent, and the recommended sequence of work. This becomes the backbone of everything that follows.",
    bullets: ["Risk-ranked findings, written for humans", "Quick wins vs. longer-term projects", "Recommended roadmap and priorities"]
  }]
}, {
  id: "contract",
  index: 2,
  cmd: "rtw partner --define-scope --agree",
  title: "Partnership",
  tag: "ALIGN",
  duration: "~3–5 days",
  summary: "We turn findings into a clear, no-surprises partnership. You'll know exactly what's covered, what it costs, and what 'done' looks like — in writing.",
  outcome: "A signed agreement with transparent pricing and clearly-defined coverage.",
  steps: [{
    label: "Scope & service design",
    cmd: "scope --services --responsibilities",
    duration: "1–2 days",
    status: "Designing",
    body: "We translate the discovery roadmap into a concrete service plan — which systems we manage, which we co-manage with your team, and where the responsibility lines sit. No grey areas.",
    bullets: ["Managed vs. co-managed boundaries", "Response targets and support hours", "Project work vs. ongoing operations"]
  }, {
    label: "Transparent pricing",
    cmd: "price --per-seat --no-surprises",
    duration: "1 day",
    status: "Pricing",
    body: "We set a fair, predictable price for the partnership — typically per-user — so your IT cost scales sensibly with your team. You see the full breakdown, with no padded line items or mystery fees.",
    bullets: ["Predictable monthly investment", "Clear breakdown of what's included", "One-time project costs called out separately"]
  }, {
    label: "Coverage & SLA review",
    cmd: "review --coverage --sla",
    duration: "1 day",
    status: "Reviewing",
    body: "We walk you through exactly what's covered — devices, users, security stack, backups, vendor management — and the service levels behind them, so expectations are mutual and documented.",
    bullets: ["Covered devices, users, and services", "Security and backup commitments", "Vendor and warranty management"]
  }, {
    label: "Sign & schedule kickoff",
    cmd: "agree --sign && schedule kickoff",
    duration: "1 day",
    status: "Confirming",
    body: "Once it looks right to you, we make it official and lock in a kickoff date. From here, the clock starts on getting you onboarded the Rapid way.",
    bullets: ["Agreement executed", "Kickoff call scheduled", "Onboarding team assigned"]
  }]
}, {
  id: "onboarding",
  index: 3,
  cmd: "rtw onboard --deploy --migrate --secure",
  title: "Onboarding",
  tag: "DEPLOY",
  duration: "~2–6 weeks",
  summary: "The hands-on phase. We deploy our tooling, build out your Microsoft 365 foundation, migrate your email and data, and lock the whole environment down — coordinating with you at every step.",
  outcome: "A modern, secured, fully-managed environment with your team working without disruption.",
  steps: [{
    label: "Deploy onboarding agent",
    cmd: "agent deploy --silent --all-endpoints",
    duration: "Day 1",
    status: "Deploying",
    body: "We push our lightweight onboarding agent to every endpoint. It quietly gives us the visibility and remote access we need to do the rest of the work without living at your desks.",
    bullets: ["Silent, no-disruption deployment", "Secure remote access established", "Asset inventory auto-syncs to our system"]
  }, {
    label: "Stand up Microsoft 365 tenant",
    cmd: "m365 tenant init --domain --dns",
    duration: "1–3 days",
    status: "Provisioning",
    body: "We establish or harden your Microsoft 365 tenant — domains verified, DNS configured, and core services like Exchange Online, SharePoint, and Teams brought online on a clean, secure foundation.",
    bullets: ["Tenant created or taken over cleanly", "Domain and DNS records configured", "Exchange, SharePoint, and Teams enabled"]
  }, {
    label: "Procure & assign licensing",
    cmd: "m365 license procure --assign --optimize",
    duration: "1–2 days",
    status: "Licensing",
    body: "We size the right Microsoft licensing for your team — not too much, not too little — procure it, and assign it. You only pay for what people actually need.",
    bullets: ["Right-sized license selection", "Procured and assigned per user", "Cost-optimized, reviewed regularly"]
  }, {
    label: "Build out users & groups",
    cmd: "identity provision --users --groups --mfa",
    duration: "2–4 days",
    status: "Provisioning",
    body: "We build every user account, security group, and shared resource — with multi-factor authentication and conditional access enforced from day one. Permissions follow least-privilege, not convenience.",
    bullets: ["User accounts and security groups", "MFA and conditional access enforced", "Least-privilege permission model"]
  }, {
    label: "Email migration",
    cmd: "migrate mail --cutover --zero-loss",
    duration: "2–5 days",
    status: "Migrating",
    body: "We migrate mailboxes, calendars, and contacts to the new environment with a planned cutover — staged and tested so nothing is lost and the switchover lands cleanly, usually overnight.",
    bullets: ["Mailboxes, calendars, and contacts moved", "Staged, tested cutover plan", "Minimal downtime, zero data loss"]
  }, {
    label: "Data migration",
    cmd: "migrate data --files --shares --verify",
    duration: "3–7 days",
    status: "Migrating",
    body: "File shares, documents, and team data move to SharePoint, OneDrive, or your chosen home. Structure and permissions are preserved, and every transfer is verified before we call it done.",
    bullets: ["File shares and documents migrated", "Permissions and structure preserved", "Integrity verified post-migration"]
  }, {
    label: "Deploy RMM agent",
    cmd: "rmm deploy --monitor --patch --automate",
    duration: "1–2 days",
    status: "Deploying",
    body: "Our remote monitoring & management agent goes on every device. From here we watch health 24/7, automate patching, and catch problems before they become outages — usually before you even notice.",
    bullets: ["24/7 health and performance monitoring", "Automated patch management", "Proactive alerting and self-healing"]
  }, {
    label: "Deploy security tools",
    cmd: "security deploy --edr --filter --harden",
    duration: "2–3 days",
    status: "Securing",
    body: "We layer in the security stack: endpoint detection & response, DNS and email filtering, and hardened configuration baselines. This is the difference between hoping you're safe and knowing it.",
    bullets: ["Endpoint detection & response (EDR)", "Email and DNS content filtering", "Hardened security baselines applied"]
  }, {
    label: "Backup configuration",
    cmd: "backup configure --m365 --endpoints --test",
    duration: "1–3 days",
    status: "Protecting",
    body: "We configure backups for Microsoft 365, servers, and critical data — then we test a restore. A backup you've never restored from isn't a backup; it's a hope. We don't deal in hope.",
    bullets: ["Microsoft 365 and endpoint backups", "Server and critical-data protection", "Restore tested and verified"]
  }, {
    label: "Documentation & handoff",
    cmd: "document --runbooks --handoff --enable",
    duration: "2–4 days",
    status: "Finalizing",
    body: "Everything we built gets documented — runbooks, credentials, network maps, and procedures — and your team gets oriented on how to reach us and what to expect. You're now fully managed and fully informed.",
    bullets: ["Complete environment documentation", "Support process and contacts shared", "Team oriented and live in production"]
  }]
}];
const comms = {
  cmd: "tail -f rtw://comms",
  title: "We talk the whole way",
  body: "Throughout every phase, you're never guessing. You get clear updates by email, scheduled check-ins, and a shared view of progress — so the journey always feels like a partnership, not a black box.",
  lines: [{
    from: "rtw" as const,
    text: "Discovery findings ready — sending the report over for review."
  }, {
    from: "you" as const,
    text: "Looks great. A few questions on the backup gaps."
  }, {
    from: "rtw" as const,
    text: "Kickoff confirmed for Monday. Onboarding team is assigned."
  }, {
    from: "rtw" as const,
    text: "Email cutover complete — everyone's live with zero data loss."
  }, {
    from: "rtw" as const,
    text: "Backups tested & verified. You're fully protected."
  }]
};
function Cmd({
  text
}: {
  text: string;
}) {
  const parts = text.split(/(\s+)/);
  return <>
      {parts.map((p, i) => p.trim().startsWith("-") ? <span key={i} className="text-[#ee8b33]">{p}</span> : <span key={i}>{p}</span>)}
    </>;
}
function StepRow({
  step
}: {
  step: Step;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border border-white/[0.08] rounded-md overflow-hidden bg-[#0d0f13]/60">
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} className="w-full flex items-center gap-3 px-4 py-3 text-left font-mono text-sm hover:bg-white/[0.03] transition-colors">
        <span className="text-[#4a8fd0] select-none">$</span>
        <span className="flex-1 text-[#d7dbe0] truncate">
          <Cmd text={step.cmd} />
        </span>
        <Plus className={`w-4 h-4 text-[#8b929d] transition-transform ${open ? "rotate-45" : ""}`} />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="px-4 pb-4 pt-1 border-t border-white/[0.06] bg-[#0a0b0d]/60">
            <div className="flex items-center gap-2 font-mono text-xs text-[#4a8fd0] mb-2">
              <span className="w-1.5 h-1.5 bg-[#4a8fd0] rounded-sm" />
              {step.status}
              <span className="text-[#565d68] ml-2">// {step.duration}</span>
            </div>
            <p className="text-[#d7dbe0]/90 text-sm leading-relaxed mb-3">
              {step.body}
            </p>
            <ul className="space-y-1.5">
              {step.bullets.map((b, i) => <li key={i} className="flex gap-2 text-sm text-[#8b929d]">
                  <span className="text-[#ee8b33] select-none">›</span>
                  <span>{b}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </div>;
}
function PhaseBlock({
  phase
}: {
  phase: Phase;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  return <div ref={ref} className={`relative pl-10 md:pl-14 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      <div className="absolute left-0 top-0 flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-md bg-[#101217] border border-white/[0.14] text-[#ee8b33] font-mono text-sm font-bold">
        0{phase.index}
      </div>

      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
        <h3 className="text-2xl md:text-3xl font-default font-bold text-white">
          {phase.title}
        </h3>
        <span className="text-xs font-mono tracking-widest text-[#ee8b33] bg-[#ee8b33]/10 px-2 py-0.5 rounded">
          {phase.tag}
        </span>
        <span className="text-xs font-mono text-[#565d68]">
          {phase.duration}
        </span>
      </div>

      <div className="font-mono text-sm text-[#d7dbe0] mb-3">
        <span className="text-[#4a8fd0] select-none">$ </span>
        <Cmd text={phase.cmd} />
      </div>

      <p className="text-[#8b929d] leading-relaxed mb-5 max-w-2xl">
        {phase.summary}
      </p>

      <div className="space-y-2">
        {phase.steps.map((s, i) => <StepRow key={i} step={s} />)}
      </div>

      <div className="mt-4 text-sm font-mono text-[#d7dbe0]">
        <span className="text-[#4a8fd0]">outcome →</span>{" "}
        <span className="text-[#8b929d]">{phase.outcome}</span>
      </div>
    </div>;
}
export default function OnboardingJourney() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll();
  const totalSteps = useMemo(() => phases.reduce((s, p) => s + p.steps.length, 0), []);
  return <section id="onboarding-journey" className="dark relative bg-[#0a0b0d] text-[#d7dbe0] py-20 md:py-32 overflow-hidden font-mono">
      {/* ambient background */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
        backgroundSize: "48px 48px"
      }} />
        <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-[#ee8b33]/10 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#4a8fd0]/10 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-6 max-w-5xl relative z-10">
        {/* hero */}
        <div ref={headerRef} className={`mb-16 md:mb-24 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="text-xs tracking-widest text-[#ee8b33] mb-4">
            // new-client onboarding
          </div>
          <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05] mb-6">
            Your journey,<br />
            mapped <span className="text-[#ee8b33]">end‑to‑end</span>.
          </h2>
          <p className="text-lg text-[#8b929d] max-w-2xl leading-relaxed mb-6">
            Welcome aboard. Here&rsquo;s exactly what partnering with Rapid Tech
            Works looks like — from the first conversation to a
            fully-managed, secured environment. Expand any step to see what
            happens under the hood.
          </p>
          <div className="inline-flex items-center gap-3 px-4 py-2.5 rounded-md border border-white/[0.08] bg-[#101217] text-sm">
            <span className="text-[#4a8fd0]">rtw@onboarding:~$</span>
            <span className="text-[#d7dbe0]">
              start onboarding <span className="text-[#ee8b33]">--interactive</span>{" "}
              <span className="text-[#ee8b33]">--client</span> you
            </span>
            <span className="inline-block w-2 h-4 bg-[#ee8b33] animate-pulse" />
          </div>

          <div className="flex flex-wrap gap-2 mt-6">
            {[{
            k: "3",
            v: "phases"
          }, {
            k: String(totalSteps),
            v: "steps"
          }, {
            v: "no surprises"
          }, {
            v: "we talk the whole way"
          }].map((c, i) => <span key={i} className="text-xs font-mono px-3 py-1.5 rounded-md border border-white/[0.08] bg-white/[0.02] text-[#8b929d]">
                {c.k && <b className="text-white mr-1">{c.k}</b>}
                {c.v}
              </span>)}
          </div>
        </div>

        {/* journey */}
        <div className="flex items-center gap-3 mb-10">
          <span className="font-mono text-xs text-[#ee8b33]">01</span>
          <h3 className="font-default text-xl text-white font-semibold">
            The Journey
          </h3>
          <span className="flex-1 h-px bg-white/[0.08]" />
        </div>

        <div className="relative space-y-14 md:space-y-20">
          <div className="absolute left-4 md:left-5 top-4 bottom-4 w-px bg-white/[0.08]" />
          {phases.map(p => <PhaseBlock key={p.id} phase={p} />)}
        </div>

        {/* communication */}
        <div className="flex items-center gap-3 mt-20 mb-8">
          <span className="font-mono text-xs text-[#ee8b33]">02</span>
          <h3 className="font-default text-xl text-white font-semibold">
            Communication
          </h3>
          <span className="flex-1 h-px bg-white/[0.08]" />
        </div>

        <div className="rounded-xl border border-white/[0.08] bg-[#101217] overflow-hidden shadow-2xl">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.08] bg-[#0d0f13]">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#ee8b33]/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#4a8fd0]/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
            </div>
            <div className="text-xs font-mono text-[#565d68]">{comms.cmd}</div>
            <div className="w-10" />
          </div>
          <div className="p-6 md:p-8">
            <h4 className="font-default text-2xl text-white font-semibold mb-2">
              {comms.title}
            </h4>
            <p className="text-[#8b929d] leading-relaxed max-w-2xl mb-6">
              {comms.body}
            </p>
            <div className="space-y-2">
              {comms.lines.map((l, i) => <div key={i} className="flex gap-3 items-start font-mono text-sm">
                  <span className={`shrink-0 w-12 select-none ${l.from === "rtw" ? "text-[#ee8b33]" : "text-[#4a8fd0]"}`}>
                    {l.from}
                  </span>
                  <span className={`px-3 py-1.5 rounded-md border ${l.from === "rtw" ? "border-[#ee8b33]/20 bg-[#ee8b33]/[0.04] text-[#d7dbe0]" : "border-[#4a8fd0]/20 bg-[#4a8fd0]/[0.04] text-[#d7dbe0]"}`}>
                    {l.text}
                  </span>
                </div>)}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-20 md:mt-28 text-center">
          <div className="text-xs font-mono tracking-widest text-[#ee8b33] mb-4">
            // ready when you are
          </div>
          <h3 className="font-default font-bold text-3xl md:text-4xl text-white tracking-tight mb-4">
            Let&rsquo;s map your onboarding.
          </h3>
          <p className="text-[#8b929d] max-w-xl mx-auto mb-8 font-sans">
            Book a no-pressure discovery call. We&rsquo;ll walk your environment,
            show you what we&rsquo;d do first, and give you the roadmap — even
            if you don&rsquo;t hire us.
          </p>
          <Button size="lg" className="rounded-full px-8 h-14 text-base shadow-lg hover:shadow-xl transition-all group bg-[#ee8b33] hover:bg-[#d9772a] text-white" asChild>
            <Link to="#contact">
              Start Your Onboarding
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}